'use client';

import { useState, useEffect, useCallback } from 'react';
import { initialStages } from '../../data/stages';
import { Stage, GameState } from '../../types/game'; // 型定義のインポートパスを修正

// useGameControllerフックのプロパティ
interface UseGameControllerProps {
  onStageClear?: (
    stageId: string,
    mistakes: number,
    timeTaken: number | null,
    score: number,
  ) => void;
  onGameOver?: () => void;
  // TODO: 効果音や画像表示のためのコールバックを追加
  // onCorrectType?: () => void;
  // onMistake?: () => void;
  // onLineClear?: ():void;
}

// 初期ステージデータ (外部ファイルに移動したためコメントアウトまたは削除)
/*
const initialStages: Stage[] = [
  {
    id: 'leg-stage-1',
    name: '脚部ユニット：基本歩行プログラム',
    description: 'ロボットが最初の一歩を踏み出すための基本コードを打ち込もう。',
    lines: [
      { code: 'Robot.walk(1)', explanation: 'ロボットが1歩前に進みます。' },
      { code: 'Robot.turn("left")', explanation: 'ロボットが左に90度回転します。' },
      { code: 'Robot.walk(2)', explanation: 'ロボットが2歩前に進みます。' },
    ],
    storyBefore: '起動シーケンス開始...脚部ユニットのテストを行います。',
    storyAfter: '脚部ユニットの基本動作を確認！次はもっと複雑な動きに挑戦だ。',
    programmingConcept: '関数呼び出し、引数',
  },
  {
    id: 'arm-stage-1',
    name: '腕部ユニット：オブジェクト操作',
    description: 'アームを操作して、物を掴むプログラムを完成させよう。',
    lines: [
      { code: 'Arm.extend()', explanation: 'アームを伸ばします。' },
      { code: 'Arm.grab()', explanation: 'アームで物を掴みます。' },
      { code: 'Arm.retract()', explanation: 'アームを縮めます。' },
    ],
    storyBefore: '腕部ユニットの調整完了。精密な作業テストを開始します。',
    storyAfter: 'オブジェクトのピックアップに成功！これで物資の運搬も可能になるぞ。',
    programmingConcept: 'オブジェクトとメソッド',
  },
  {
    id: 'head-stage-1',
    name: '頭部ユニット：センサー起動',
    description: '周囲の状況を把握するためのセンサーを起動させよう。',
    lines: [
      { code: 'Head.sensor("on")', explanation: '頭部のセンサーをオンにします。'},
      { code: 'Head.scan("area")', explanation: '周囲のエリアをスキャンします。'},
      { code: 'Report.status()', explanation: 'スキャン結果のステータスを表示します。'}
    ],
    storyBefore: '頭部ユニットの最終チェック。視覚センサーと環境スキャナーを起動する。',
    storyAfter: 'センサーシステム正常稼働！これでロボットは周囲の状況を認識できるようになった。',
    programmingConcept: '状態変化、情報取得'
  }
];
*/

const CLEARED_STAGES_STORAGE_KEY = 'clearedStageIds';

export const useGameController = (props: UseGameControllerProps = {}) => {
  const { onStageClear, onGameOver } = props;

  const [stages] = useState<Stage[]>(initialStages);
  const [currentStageId, setCurrentStageId] = useState<string | null>(null); // 初期値を null に変更
  const [currentStage, setCurrentStage] = useState<Stage | null>(null);
  const [clearedStageIds, setClearedStageIds] = useState<string[]>([]);

  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [gameState, setGameState] = useState<GameState>('initial'); // 初期状態を 'initial' に変更
  const [mistakes, setMistakes] = useState(0);
  const [stageStartTime, setStageStartTime] = useState<number | null>(null);
  const [unpauseTime, setUnpauseTime] = useState<number | null>(null); // 一時停止解除時の時間
  const [pausedTime, setPausedTime] = useState<number>(0); // 合計一時停止時間
  const [pauseStartTime, setPauseStartTime] = useState<number | null>(null); // 一時停止開始時間
  const [stageTimeTaken, setStageTimeTaken] = useState<number | null>(null);
  const [score, setScore] = useState<number>(0); // スコアの状態を追加
  const [showHint, setShowHint] = useState(false); // ヒント表示状態
  const [completedStageLines, setCompletedStageLines] = useState<string[]>([]); // 入力完了した行のコードを保持
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [gameStartTime, setGameStartTime] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [gameEndTime, setGameEndTime] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [stageClearTimes, setStageClearTimes] = useState<number[]>([]);
  // const [typingSpeedKPM, setTypingSpeedKPM] = useState<number[]>([]); // 削除
  // const [accuracy, setAccuracy] = useState<number[]>([]); // 削除
  const [gameSessionStartTime, setGameSessionStartTime] = useState<number | null>(null);
  const [gameSessionEndTime, setGameSessionEndTime] = useState<number | null>(null);

  // セッション全体の統計情報用 state
  const [sessionTotalChars, setSessionTotalChars] = useState<number>(0);
  const [sessionTotalTimeTaken, setSessionTotalTimeTaken] = useState<number>(0);
  const [sessionTotalMistakes, setSessionTotalMistakes] = useState<number>(0);

  // ゲームセッション全体の統計情報を計算
  const gameSession = (() => {
    if (!gameSessionStartTime) return null;

    const totalKPM =
      sessionTotalTimeTaken > 0 ? (sessionTotalChars / sessionTotalTimeTaken) * 60 : 0;
    const totalAccuracy =
      sessionTotalChars + sessionTotalMistakes > 0
        ? (sessionTotalChars / (sessionTotalChars + sessionTotalMistakes)) * 100
        : 0;

    return {
      startTime: gameSessionStartTime,
      endTime: gameSessionEndTime,
      totalKPM: Math.round(totalKPM),
      totalAccuracy: Math.round(totalAccuracy),
      // 他に必要なセッション情報があればここに追加
    };
  })();

  // Load cleared stages from localStorage on mount
  useEffect(() => {
    const storedClearedStages = localStorage.getItem(CLEARED_STAGES_STORAGE_KEY);
    if (storedClearedStages) {
      setClearedStageIds(JSON.parse(storedClearedStages));
    }
  }, []);

  // Save cleared stages to localStorage when updated
  useEffect(() => {
    localStorage.setItem(CLEARED_STAGES_STORAGE_KEY, JSON.stringify(clearedStageIds));
  }, [clearedStageIds]);

  // currentStageId が変更された時の副作用
  useEffect(() => {
    if (currentStageId) {
      const stage = stages.find((s) => s.id === currentStageId);
      if (stage) {
        setCurrentStage(stage);
        setCurrentLineIndex(0);
        setUserInput('');
        setMistakes(0); // ミスをリセット
        setScore(0);
        setStageStartTime(null);
        setUnpauseTime(null);
        setPausedTime(0);
        setPauseStartTime(null); // 追加
        setStageTimeTaken(null);
        setShowHint(false);
        setCompletedStageLines([]); // ステージ変更時にリセット
        setGameState('ready');
      } else {
        console.error(`Stage with id "${currentStageId}" not found.`);
        setCurrentStageId(null);
        setCurrentStage(null);
        setGameState('initial'); // ステージが見つからない場合は初期状態に戻る
      }
    } else {
      // currentStageId が null の場合 (ゲーム開始前、全ステージクリア後など)
      setCurrentStage(null);
      // gameState は initializeGame や goToNextStage で適切に設定される
    }
  }, [currentStageId, stages]);

  const currentLine = currentStage?.lines[currentLineIndex];

  const handleInputChange = useCallback(
    (typedValue: string) => {
      if (gameState !== 'playing' || !currentLine) return;

      const prevUserInput = userInput;
      setUserInput(typedValue);

      // 行頭スペースをトリムした比較用のテキストを作成
      const trimmedCurrentLineCode = currentLine.code.trimStart();
      const trimmedTypedValue = typedValue.trimStart();
      const trimmedPrevUserInput = prevUserInput.trimStart(); // ミス判定用に前回の入力もトリム

      // Mistake detection logic:
      // Only count mistakes if a character was added.
      // ミス判定もトリムされた値で行う
      if (trimmedTypedValue.length > trimmedPrevUserInput.length) {
        const typedCharIndex = trimmedTypedValue.length - 1;
        const typedChar = trimmedTypedValue[typedCharIndex];

        if (typedCharIndex < trimmedCurrentLineCode.length) {
          const targetChar = trimmedCurrentLineCode[typedCharIndex];
          if (typedChar !== targetChar) {
            setMistakes((prev) => prev + 1); // Increment stage mistakes
          }
        } else {
          // User typed beyond the length of the target code for the line (after trimming)
          setMistakes((prev) => prev + 1); // Count as a mistake
        }
      }

      // 正誤判定もトリムされた値で行う
      if (trimmedTypedValue === trimmedCurrentLineCode) {
        setCompletedStageLines((prevLines) => [...prevLines, currentLine.code]); // 完了した行は元のコードを保存
        if (currentLineIndex < (currentStage?.lines.length || 0) - 1) {
          setCurrentLineIndex((prev) => prev + 1);
          setUserInput(''); // 次の行に行く際にユーザー入力をクリア
          setShowHint(false);
        } else {
          setGameState('result');
          const endTime = Date.now();
          const timeTaken = stageStartTime
            ? Math.round((endTime - stageStartTime - pausedTime) / 1000)
            : null;
          setStageTimeTaken(timeTaken);

          const calculatedScore = Math.max(0, 1000 - mistakes * 50 - (timeTaken ?? 0) * 2);
          setScore(calculatedScore);

          if (currentStage && timeTaken !== null && timeTaken > 0) {
            const stageTotalChars = currentStage.lines.reduce(
              (sum, line) => sum + line.code.trim().length,
              0,
            );
            // setTypingSpeedKPM((prevKPM) => [...prevKPM, currentKPM]); // 削除
            // setAccuracy((prevAcc) => [...prevAcc, currentAccuracy]); // 削除
            setSessionTotalChars((prev) => prev + stageTotalChars);
            setSessionTotalTimeTaken((prev) => prev + timeTaken);
            setSessionTotalMistakes((prev) => prev + mistakes); // mistakes は現在のステージのミス数
          }

          if (currentStage && !clearedStageIds.includes(currentStage.id)) {
            setClearedStageIds((prev) => [...prev, currentStage.id]);
          }

          onStageClear?.(currentStage!.id, mistakes, timeTaken, calculatedScore);
        }
      }
    },
    [
      gameState,
      currentLine,
      userInput,
      currentLineIndex,
      currentStage,
      onStageClear,
      mistakes,
      stageStartTime,
      clearedStageIds,
      pausedTime,
      setUserInput,
      setMistakes,
      setCurrentLineIndex,
      setShowHint,
      setCompletedStageLines,
      setGameState,
      setStageTimeTaken,
      setScore,
      setClearedStageIds,
    ],
  );

  const startGame = useCallback(() => {
    if (!currentStage || gameState === 'playing' || gameState === 'initial') return; // 'initial' 状態でも開始しない

    setCompletedStageLines([]); // ゲーム開始時にリセット
    setCurrentLineIndex(0);
    setUserInput('');
    setMistakes(0); // ミスをリセット
    setStageStartTime(Date.now());
    setUnpauseTime(Date.now()); // 開始時を一時停止解除時間として初期化
    setPausedTime(0); // 一時停止時間をリセット
    setPauseStartTime(null); // ゲーム開始時に一時停止開始時間をリセット
    setStageTimeTaken(null);
    setScore(0);
    setShowHint(false); // ゲーム開始時にヒントを非表示
    setGameState('playing');

    console.log('Game started');
    setCurrentStageIndex(0);
    setGameStartTime(Date.now());
    setGameEndTime(null);
    setStageClearTimes([]);
    // setTypingSpeedKPM([]); // 削除
    // setAccuracy([]); // 削除
    if (gameSessionStartTime === null) {
      setGameSessionStartTime(Date.now());
      // 新しいセッション開始時に統計情報をリセット
      setSessionTotalChars(0);
      setSessionTotalTimeTaken(0);
      setSessionTotalMistakes(0);
    }
    setGameSessionEndTime(null); // ゲーム開始時にセッション終了時刻をリセット
  }, [currentStage, gameState, gameSessionStartTime]);

  const viewAllClearedScreen = () => {
    setGameState('all_cleared');
  };

  const initializeGame = useCallback(() => {
    setGameState('loading');
    setCompletedStageLines([]);
    const storedClearedStages = localStorage.getItem(CLEARED_STAGES_STORAGE_KEY);
    const loadedClearedStageIds = storedClearedStages ? JSON.parse(storedClearedStages) : [];
    setClearedStageIds(loadedClearedStageIds);

    console.log('[Debug] initializeGame called');
    console.log('[Debug] initialStages:', initialStages); // インポートされた生のステージデータ
    console.log('[Debug] stages (state):', stages); // useStateで管理されているステージデータ
    console.log('[Debug] loadedClearedStageIds from localStorage:', loadedClearedStageIds);

    let nextStageId: string | null = null;
    if (stages.length > 0) {
      const lastClearedStageIndex = stages.findIndex(
        (s) => s.id === loadedClearedStageIds[loadedClearedStageIds.length - 1],
      );

      console.log('[Debug] stages.length:', stages.length);
      console.log('[Debug] lastClearedStageIndex:', lastClearedStageIndex);

      if (loadedClearedStageIds.length === 0) {
        console.log('[Debug] No stages cleared yet, starting from first stage.');
        nextStageId = stages[0].id;
      } else if (lastClearedStageIndex !== -1 && lastClearedStageIndex < stages.length - 1) {
        console.log('[Debug] Last cleared stage found, moving to next stage.');
        nextStageId = stages[lastClearedStageIndex + 1].id;
      } else if (lastClearedStageIndex === stages.length - 1) {
        console.log('[Debug] All stages cleared.');
        setGameState('all_cleared');
        setCurrentStageId(null);
        onGameOver?.();
        return;
      } else {
        console.warn(
          '[Debug] Cleared stages data might be inconsistent or last cleared stage not found in current stages. Starting from the first stage.',
        );
        nextStageId = stages[0].id;
      }
    } else {
      console.log('[Debug] No stages available in stages array. Setting to all_cleared.');
      setGameState('all_cleared');
      setCurrentStageId(null);
      onGameOver?.();
      return; // stages.length === 0 の場合、ここで処理を終了
    }

    console.log('[Debug] nextStageId determined:', nextStageId);

    if (nextStageId) {
      setCurrentStageId(nextStageId);
    } else {
      // このケースは上のロジックでカバーされるはずだが、念のため
      console.log('[Debug] No nextStageId found, though stages exist. Setting to all_cleared.');
      setGameState('all_cleared');
      setCurrentStageId(null);
      onGameOver?.();
    }
  }, [
    stages,
    onGameOver,
    setGameState,
    setCompletedStageLines,
    setClearedStageIds,
    setCurrentStageId,
  ]); // 依存配列を更新

  const retryCurrentStage = useCallback(() => {
    if (currentStageId) {
      const stage = stages.find((s) => s.id === currentStageId);
      if (stage) {
        setCurrentStage(stage);
        setCompletedStageLines([]); // リトライ時にリセット
        setCurrentLineIndex(0);
        setUserInput('');
        setMistakes(0); // ミスをリセット
        setStageStartTime(null);
        setPauseStartTime(null); // リトライ時に一時停止開始時間をリセット
        setStageTimeTaken(null);
        setScore(0);
        setShowHint(false);
        setGameState('ready');
      } else {
        console.error(`Stage with id "${currentStageId}" not found for retry.`);
        setCurrentStageId(null); // ステージ選択に戻る代わりに初期状態へ
        setGameState('initial');
      }
    }
  }, [currentStageId, stages]);

  const goToNextStage = useCallback(() => {
    // kpm と acc パラメータを削除
    if (!currentStageId) {
      initializeGame(); // 現在のステージがない場合（例：結果画面から直接呼ばれた場合など）、ゲームを初期化
      return;
    }

    const currentStageIdx = stages.findIndex((s) => s.id === currentStageId);

    if (currentStageIdx === -1) {
      // 現在のステージIDが不正な場合
      console.error('Invalid current stage ID for goToNextStage.');
      initializeGame(); // ゲームを初期化
      return;
    }

    if (currentStageIdx < stages.length - 1) {
      // 次のステージがある場合
      const nextStage = stages[currentStageIdx + 1];
      setCurrentStageId(nextStage.id); // これにより useEffect がトリガーされ、gameState が 'ready' になる
    } else {
      // All stages cleared
      console.log('All stages cleared');
      const endTime = Date.now();
      setGameEndTime(endTime);
      if (gameSessionEndTime === null) {
        setGameSessionEndTime(endTime);
      }
      setGameState('all_cleared');
      setCurrentStageId(null);
      onGameOver?.();
    }
  }, [currentStageId, stages, onGameOver, initializeGame, gameSessionEndTime]);

  const requestHint = useCallback(() => {
    if (gameState !== 'playing' || !currentLine) return;

    // ヒント表示ロジック
    setShowHint((prev) => !prev);
  }, [gameState, currentLine]);

  const resetProgress = useCallback(() => {
    localStorage.removeItem(CLEARED_STAGES_STORAGE_KEY);
    setClearedStageIds([]);
    setCurrentStageId(null);
    setCurrentStage(null);
    setGameState('initial');
    // セッション関連のstateもリセット
    setGameSessionStartTime(null);
    setGameSessionEndTime(null);
    setSessionTotalChars(0);
    setSessionTotalTimeTaken(0);
    setSessionTotalMistakes(0);
    console.log('Game progress reset.');
  }, []);

  const goToTitle = useCallback(() => {
    setCurrentStageId(null);
    setCurrentStage(null);
    setGameState('initial');
    // 必要に応じてセッション情報をリセットするかどうかは要件による
    // ここではリセットしないが、ゲームデザインによってはリセットも検討
    console.log('Returned to title screen.');
  }, []);

  const pauseGame = useCallback(() => {
    if (gameState === 'playing') {
      setGameState('paused');
      setPauseStartTime(Date.now()); // 一時停止開始時間を記録
      console.log('Game paused');
    }
  }, [gameState]);

  const resumeGame = useCallback(() => {
    if (gameState === 'paused' && pauseStartTime) {
      const currentPausedDuration = Date.now() - pauseStartTime;
      setPausedTime((prev) => prev + currentPausedDuration); // 一時停止時間を加算
      setUnpauseTime(Date.now()); // 一時停止解除時間を更新
      setGameState('playing');
      setPauseStartTime(null); // 一時停止開始時間をリセット
      console.log('Game resumed');
    }
  }, [gameState, pauseStartTime]);

  return {
    stages,
    currentStage,
    currentLine,
    gameState,
    mistakes,
    stageTimeTaken,
    score,
    showHint,
    clearedStageIds,
    gameSession,
    gameSessionStartTime,
    gameSessionEndTime,
    // App.tsx で分割代入するために追加
    currentLineIndex,
    userInput,
    completedStageLines,
    // アクションをまとめる
    actions: {
      startGame,
      viewAllClearedScreen,
      initializeGame,
      retryCurrentStage,
      goToNextStage,
      requestHint,
      handleInputChange,
      resetProgress, // 追加
      goToTitle, // 追加
      pauseGame, // 追加
      resumeGame, // 追加
    },
  };
};
